
import { Injectable } from '@angular/core';

import {
    PlanningAssignment,
    ResourceEmployee
} from '../Models/resource.models';

export interface ResourceDayCapacity {
    date: string;
    availableHours: number;
    plannedHours: number;
    utilizationPercent: number;
    overloaded: boolean;
}

export interface ResourceCapacitySummary {
    availableHours: number;
    plannedHours: number;
    utilizationPercent: number;
    overloadedDays: number;
}

@Injectable({
    providedIn: 'root'
})
export class ResourceCapacityService {

    private readonly workStartMinutes = 8 * 60;

    getDayCapacity(
        employee: ResourceEmployee,
        date: string,
        assignments: PlanningAssignment[]
    ): ResourceDayCapacity {

        const day = this.parseDate(date).getDay();
        const isWeekend = day === 0 || day === 6;

        const availableHours = 7.7;

        let plannedHours = 0;

        for (const assignment of assignments) {
            plannedHours += this.getAssignmentHours(
                assignment,
                employee,
                date
            );
        }

        const utilizationPercent = availableHours > 0
            ? (plannedHours / availableHours) * 100
            : plannedHours > 0 ? 100 : 0;

        return {
            date,
            availableHours,
            plannedHours: this.round(plannedHours),
            utilizationPercent: this.round(utilizationPercent),
            overloaded: plannedHours > availableHours + 0.000001
        };
    }


    getSummary(
        employee: ResourceEmployee,
        dates: string[],
        assignments: PlanningAssignment[]
    ): ResourceCapacitySummary {

        let availableHours = 0;
        let plannedHours = 0;
        let overloadedDays = 0;

        for (const date of dates) {

            const dayCapacity = this.getDayCapacity(
                employee,
                date,
                assignments
            );

            const dayOfWeek = this.parseDate(date).getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

            // Nur Montag bis Freitag zählen zur
            // regulären Wochen- und Monatskapazität.
            if (!isWeekend) {
                availableHours += 7.7;
            }

            // Geplante Stunden zählen immer,
            // auch bei ausdrücklich geplanter Wochenendarbeit.
            plannedHours += dayCapacity.plannedHours;

            // Tagesüberlastungen zählen unabhängig
            // von der durchschnittlichen Auslastung.
            if (dayCapacity.overloaded) {
                overloadedDays++;
            }
        }

        const utilizationPercent = availableHours > 0
            ? (plannedHours / availableHours) * 100
            : plannedHours > 0 ? 100 : 0;

        return {
            availableHours: this.round(availableHours),
            plannedHours: this.round(plannedHours),
            utilizationPercent: this.round(utilizationPercent),
            overloadedDays
        };
    }



    getAssignmentHours(
        assignment: PlanningAssignment,
        employee: ResourceEmployee,
        date: string
    ): number {

        // Liegt der Tag außerhalb des Termins?
        if (
            date < assignment.startDate ||
            date > assignment.endDate
        ) {
            return 0;
        }

        // Ist der Mitarbeiter diesem Termin zugeordnet?
        const participant = assignment.participants.find(
            p => p.employeeId === employee.id
        );

        if (!participant) {
            return 0;
        }

        const day = this.parseDate(date).getDay();
        const isWeekend = day === 0 || day === 6;

        // Wochenenden werden nur berücksichtigt,
        // wenn sie ausdrücklich eingeplant wurden.
        if (isWeekend) {
            if (!assignment.weekendDates.includes(date)) {
                return 0;
            }

            const weekendDay = assignment.weekendDays?.find(
                d => d.date === date
            );

            return Math.max(
                0,
                weekendDay?.hoursByEmployee[employee.id] ?? 0
            );
        }

        // Jeder regulär eingeplante Tag hat maximal
        // 7,7 Stunden pro Termin bei 100 % Auslastung.
        const dailyCapacity = 7.7;

        // Eintägige Termine:
        // Tatsächliche Dauer, maximal 7,7 Stunden.
        if (assignment.startDate === assignment.endDate) {

            const start = this.timeToMinutes(assignment.startTime);
            const end = this.timeToMinutes(assignment.endTime);

            const durationHours = Math.max(
                0,
                (end - start) / 60
            );

            return Math.min(durationHours, dailyCapacity) *
                participant.utilizationPercent / 100;
        }

        // Mehrtägige Termine:
        // Jeder eingeplante Werktag zählt mit 7,7 Stunden.
        return dailyCapacity *
            participant.utilizationPercent / 100;
    }


    getOverloadWarnings(
        employees: ResourceEmployee[],
        assignments: PlanningAssignment[],
        startDate: string,
        endDate: string
    ): string[] {

        const warnings: string[] = [];
        const dates = this.dateRange(startDate, endDate);

        for (const employee of employees) {
            for (const date of dates) {
                const result = this.getDayCapacity(
                    employee,
                    date,
                    assignments
                );

                if (result.overloaded) {
                    warnings.push(
                        `${employee.name}: ${result.plannedHours} h geplant ` +
                        `bei ${result.availableHours} h Kapazität ` +
                        `am ${date}`
                    );
                }
            }
        }

        return warnings;
    }

    private dateRange(start: string, end: string): string[] {
        const dates: string[] = [];
        const current = this.parseDate(start);
        const last = this.parseDate(end);

        while (current <= last) {
            dates.push(this.dateKey(current));
            current.setDate(current.getDate() + 1);
        }

        return dates;
    }

    private parseDate(value: string): Date {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    private dateKey(date: Date): string {
        return [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, '0'),
            String(date.getDate()).padStart(2, '0')
        ].join('-');
    }

    private timeToMinutes(value: string): number {
        const [hours, minutes] = value.split(':').map(Number);
        return hours * 60 + minutes;
    }

    private round(value: number): number {
        return Math.round(value * 100) / 100;
    }
}
